import {LogoApple, LogoGoogle} from '@styled-icons/ionicons-solid'
import {SiteFooter} from 'components/shared-site/footer'
import {useRouter} from 'next/router'
import {setAuthState} from 'plugins/auth-state'
import {setIndexCookie} from 'plugins/index-cookie'
import sleep from 'plugins/sleep'
import React, {useState} from 'react'
import {createAccessToken} from 'services/api/auth'
import {auth, config} from 'services/firebase'
import {firebaseAuth} from 'services/firebase-packages'
import Image from 'next/image'

type AuthStatus = 'pending' | 'error' | 'success' | 'email'

export default function Auth() {
  const router = useRouter()

  const redirect: string | null = (router.query.redirect as string) || null
  const clientToken: string | null = (router.query.client_token as string) || null

  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [email, setEmail] = useState('')

  const syncAuthState = () => {
    // We need to call this after user-interaction, otherwise
    // the localStorage doesn't persist
    setAuthState({
      redirect: redirect === 'back' ? document.referrer : redirect,
      clientToken,
      email,
    })
  }

  const authWithProvider = async (provider: firebaseAuth.AuthProvider) => {
    syncAuthState()

    let userCredential: firebaseAuth.UserCredential

    try {
      userCredential = await firebaseAuth.signInWithPopup(auth, provider)
    } catch (error: any) {
      setAuthStatus('error')
      setErrorMessage(error?.message)
      return
    }

    if (!userCredential?.user) {
      setAuthStatus('error')
      return
    }

    setIndexCookie()

    if (clientToken) {
      setAuthStatus('pending')

      await createAccessToken(clientToken)

      // We need to sleep to allow the client time to
      // pick the created access-token
      await sleep(4000)

      setAuthStatus('success')
    } else if (redirect === 'back') {
      router.back()
    } else {
      router.push('/app')
    }
  }

  const authWithGoogle = async () => {
    const provider = new firebaseAuth.GoogleAuthProvider()
    config.googleScopes.forEach((scope: string) => provider.addScope(scope))
    provider.setCustomParameters({prompt: 'select_account'})
    await authWithProvider(provider)
  }

  const authWithApple = async () => {
    const provider = new firebaseAuth.OAuthProvider('apple.com')
    config.appleScopes.forEach((scope: string) => provider.addScope(scope))
    await authWithProvider(provider)
  }

  const authWithEmail = async () => {
    setAuthStatus('email')
    syncAuthState()

    try {
      await firebaseAuth.sendSignInLinkToEmail(auth, email, {
        handleCodeInApp: true,
        url: `${location.origin}/auth/callback/email`,
      })
    } catch (error: any) {
      setAuthStatus('error')
      setErrorMessage(error?.message)
      return
    }
  }

  const resetAuthStatus = () => {
    setAuthStatus(null)
    setErrorMessage(null)
  }

  return (
    <div className="flex flex-col min-h-screen overflow-hidden bg-home-back text-white">
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="pt-16 pb-12">
            <div className="text-center">
              <Image
                src="/static/home/footer-logo.png"
                width={768 / 2}
                height={480 / 2}
              />

              <div className="max-w-3xl mx-auto">
                <div className="max-w-xs mx-auto sm:max-w-none sm:flex sm:justify-center md:mt-5">
                  <div className="min-w-72 min-h-80">
                    {!authStatus && (
                      <div>
                        <div>
                          <div>
                            <p className="text-base font-medium text-white">
                              Sign in/up with
                            </p>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                              <div>
                                <a
                                  onClick={() => authWithGoogle()}
                                  title="Sign in with Google"
                                  className="block w-full font-semibold text-white text-center bg-gradient-to-b from-home-button-lg to-home-button-rg rounded-lg drop-shadow-lg px-4 py-3 shadow-inner-white transform active:translate-y-0.5 active:opacity-90 transition duration-100 text-lg cursor-pointer"
                                >
                                  <LogoGoogle width={28} height={28} />
                                </a>
                              </div>

                              <div>
                                <a
                                  onClick={() => authWithApple()}
                                  title="Sign in with Apple"
                                  className="block w-full font-semibold bg-gradient-to-b from-home-button-lg to-home-button-rg rounded-lg drop-shadow-lg px-4 py-3 shadow-inner-white transform active:translate-y-0.5 active:opacity-90 transition duration-100 text-lg cursor-pointer text-white text-center"
                                >
                                  <LogoApple width={28} height={28} />
                                </a>
                              </div>
                            </div>
                          </div>

                          <div className="mt-10 relative">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-bluegray-800"></div>
                            </div>
                            <div className="relative flex justify-center text-base">
                              <span className="px-3 py-1 bg-home-back rounded-lg text-white text-opacity-80">
                                or continue with
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="mt-8">
                          <form
                            onSubmit={(event) => {
                              event.preventDefault()
                              authWithEmail()
                            }}
                            className="space-y-3"
                          >
                            <div>
                              <div className="mt-1">
                                <input
                                  value={email}
                                  onChange={(event) =>
                                    setEmail(event.currentTarget.value)
                                  }
                                  placeholder="your@email.com"
                                  name="email"
                                  type="email"
                                  autoComplete="email"
                                  required
                                  className="appearance-none block w-full px-4 py-3 rounded-lg shadow-sm placeholder-bluegray-400 focus:outline-none bg-bluegray-900 bg-opacity-50 border border-bluegray-800 focus:border-purple-400 focus:ring focus:ring-purple-500 focus:ring-opacity-50 transition duration-300 text-base text-center"
                                />
                              </div>
                            </div>

                            <div>
                              <button
                                type="submit"
                                className="block w-full font-semibold text-white bg-gradient-to-b from-home-button-lg to-home-button-rg rounded-lg drop-shadow-lg px-4 py-2 shadow-inner-white transform active:translate-y-0.5 active:opacity-90 transition duration-100 text-lg"
                              >
                                Continue
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}

                    {authStatus === 'pending' && (
                      <h4 className="text-center pt-10 text-2xl">Just one second...</h4>
                    )}

                    {authStatus === 'email' && (
                      <>
                        <h4 className="text-center pt-10 text-4xl">
                          You&apos;ve got mail
                        </h4>

                        <p className="text-center pt-10 text-2xl">
                          We&apos;ve emailed you a secure login link.
                        </p>
                      </>
                    )}

                    {authStatus === 'success' && (
                      <div className="space-y-4">
                        <h2 className="text-3xl text-green-500">
                          Signed in successfully
                        </h2>
                        <p className="text-center text-xl">You may close this window.</p>
                      </div>
                    )}

                    {authStatus === 'error' && (
                      <div className="space-y-4">
                        <h2 className="text-3xl text-red-500">Error signing in</h2>

                        <p className="text-center text-xl">
                          {errorMessage || 'Something went wrong.'}
                        </p>

                        <div>
                          <button
                            onClick={() => resetAuthStatus()}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-500 transition duration-300 focus:outline-none focus:ring"
                          >
                            Try again
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed inset-0 flex items-center justify-center sm:hidden bg-white px-10">
        <div className="text-center space-y-5 text-black max-w-52">
          <h3>Please use our iOS app for the best mobile experience.</h3>

          <footer>
            <a className="text-blue-500" href="https://reflect.app/testflight">
              reflect.app/testflight
            </a>
          </footer>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
